import React, { useMemo } from "react"

import type { AvailableTextTypography } from "@front-shared/types/elements.types.ts"

import { NIRVANA_COLORS, NIRVANA_THEME } from "../config/theme.config.ts"
import { useIsMobile } from "../hooks/isMobile.hook"

export const PrimaryText: React.FunctionComponent<
    IPrimaryTextProps
> = props => {
    const isMobile = useIsMobile()

    const getTypographyStyles = (): React.CSSProperties => {
        const fontFamily =
            props.fontFamily ||
            (props.weight === "bold"
                ? "PPMoriBold"
                : props.weight === "semibold"
                  ? "PPMoriSemibold"
                  : props.weight === "medium"
                    ? "PPMoriMedium"
                    : props.weight === "light"
                      ? "PPMoriLight"
                      : undefined)

        switch (props.typography) {
            case "inherit":
                return {}

            case "h1":
                return getTextStyles(
                    44,
                    56,
                    fontFamily || "PPMoriMedium",
                    props.color || NIRVANA_COLORS.brand.primary,
                    undefined,
                    0.8,
                )

            case "h2":
                return getTextStyles(
                    38,
                    48,
                    fontFamily || "PPMoriMedium",
                    props.color || NIRVANA_COLORS.brand.primary,
                    undefined,
                    0.8,
                )

            case "h3":
                return getTextStyles(
                    33,
                    40,
                    fontFamily || "PPMori",
                    props.color || NIRVANA_COLORS.brand.primary,
                )

            case "h4":
            case "h4Medium":
                return getTextStyles(
                    24,
                    32,
                    fontFamily ||
                        (props.typography === "h4Medium"
                            ? "PPMoriMedium"
                            : "PPMori"),
                    props.color || NIRVANA_COLORS.brand.primary,
                )

            case "h5":
                return getTextStyles(
                    20,
                    24,
                    fontFamily || "PPMoriMedium",
                    props.color || NIRVANA_COLORS.brand.primary,
                )

            case "h6":
                return getTextStyles(
                    14,
                    24,
                    fontFamily || "PPMoriSemibold",
                    props.color || NIRVANA_COLORS.brand.primary,
                )

            case "buttonText":
                return getTextStyles(
                    16,
                    16,
                    fontFamily || "PPMoriMedium",
                    props.color || NIRVANA_COLORS.brand.primary,
                )

            case "caption":
            case "captionSemibold":
                return getTextStyles(
                    12,
                    16,
                    fontFamily ||
                        (props.typography === "captionSemibold"
                            ? "PPMoriSemibold"
                            : "PPMori"),
                    props.color || NIRVANA_COLORS.brand.primary,
                )

            case "subtitle":
            case "subtitleSemibold":
                return getTextStyles(
                    18,
                    24,
                    fontFamily ||
                        (props.typography === "subtitleSemibold"
                            ? "PPMoriSemibold"
                            : "PPMori"),
                    props.color || NIRVANA_COLORS.brand.primary,
                )

            case "textSmall":
            case "textSmallSemibold":
            case "textSmallBold":
                return getTextStyles(
                    13,
                    21,
                    fontFamily ||
                        (props.typography === "textSmallSemibold"
                            ? "PPMoriSemibold"
                            : props.typography === "textSmallBold"
                              ? "PPMoriBold"
                              : "PPMori"),
                    props.color || NIRVANA_COLORS.brand.primary,
                )

            case "text":
            case "textSemibold":
            case "textBold":
            default:
                return getTextStyles(
                    props.size || NIRVANA_THEME.defaultFontSize,
                    props.lineHeight || NIRVANA_THEME.defaultLineHeight,
                    fontFamily ||
                        (props.typography === "textSemibold"
                            ? "PPMoriSemibold"
                            : props.typography === "textBold"
                              ? "PPMoriBold"
                              : "PPMori"),
                    props.color || NIRVANA_COLORS.brand.primary,
                )
        }
    }

    const getTextStyles = (
        fontSize: number,
        lineHeight: number,
        fontFamily: string,
        color?: string,
        letterSpacing?: string,
        mobileDeviceMultiplier = 1, // number by which fs and lh will be multiplied for small devices
    ): React.CSSProperties => ({
        fontSize: `${
            isMobile && !props.ignoreMobileSizeMultiplier
                ? mobileDeviceMultiplier * fontSize
                : fontSize
        }px`,
        lineHeight: `${
            isMobile && !props.ignoreMobileSizeMultiplier
                ? mobileDeviceMultiplier * lineHeight
                : lineHeight
        }px`,
        fontFamily: `${fontFamily}, sans-serif`,

        ...(color
            ? {
                  color,
              }
            : {}),

        ...(letterSpacing
            ? {
                  letterSpacing,
              }
            : {}),
    })

    const memoizedStyles = useMemo<React.CSSProperties>(
        () => ({
            ...(props.centered ? { textAlign: "center" } : {}),
            ...(props.underline ? { textDecoration: "underline" } : {}),
            ...(props.italic ? { fontStyle: "italic" } : {}),

            ...getTypographyStyles(),
        }),
        [props, isMobile],
    )

    return props.isHtml ? (
        <div
            className={props.className}
            style={memoizedStyles}
            dangerouslySetInnerHTML={{
                __html: props.children?.toString() || "",
            }}
        />
    ) : (
        <div className={props.className} style={memoizedStyles}>
            {props.children}
        </div>
    )
}

interface IPrimaryTextProps {
    children?: React.ReactNode
    className?: string
    fontFamily?: string
    centered?: boolean
    color?: string
    size?: number
    lineHeight?: number
    ignoreMobileSizeMultiplier?: boolean
    weight?: "light" | "normal" | "medium" | "semibold" | "bold"
    typography?: AvailableTextTypography
    underline?: boolean
    italic?: boolean
    isHtml?: boolean
}
